import { Fragment, useContext, useEffect, useState } from "react";
import { IParentData } from "../../../analytics/pages/report-details/report-details";
import NormalTable, { ITableRowData, IColumn, IScale } from "../../../shared/components/normal-table/normal-table";
import Spinner from "../../../shared/components/spinner/spinner";
import { IBlock } from "../../../shared/components/widgets-layout/widgets-layout";
import AuthContext from "../../../shared/context/auth-context";
import {useHttpNonTable} from "../../../shared/hooks/use-httpNonTable";
import { postSingleNonTable } from "../../../shared/utils/api";
import { allUniqueManagementModelDataColsKeys, allUniqueManagementModelDataOverallColsKeys, modelManagementModelDataOverallCols, modelManagementModelDataImportCols, modelManagementModelDataRecordCols} from "../../../shared/utils/static-values";
import { validateLoadingStatusNew, convertKeyIntoName } from "../../../shared/utils/utils";

export interface IRowStatus {
  opt_thresh_id?: string,
  approved_id?: string
}

export interface ITableConfig {
    unblockInput?: boolean;
    allColumns?: any; // eslint-disable-line
    dataUrl?: string;
    hideButtons: string[];
    allStatus?: string | null;
    hideSearchInput?: boolean;
    allStatusDelete?: string | null;
    hideCheckBoxes?: boolean;
    timeframeStartTilEnd?: boolean;
    clickRowToSelectMode?: boolean;
    scale?: IScale;
    lock?: boolean;
    widgetData?: IBlock;
    selectedThreshold?: string;
    selectedSource?: string;
    defaultSortCol?: string;
    orderSettings?: IParentData;
    initiallySelectedRowsIds?: string[];
    draggingStarted?: boolean;
    statusFields?: IRowStatus;
    getResultDataByColsStatus?: string | null | undefined;
    widgetId?: string;
    tobeDeselected?: string[];
    lastChangedWidget?: ITableRowData;
    verticallyHighlighted?: string[];
    getDraggingStatus?: (toggle: boolean) => void;
    getSelectedRowsData?: (data: ITableRowData[]) => void;
    handleOnDelete?: (ids: string[]) => void;
    getNewColumnsOrder?: (data: IParentData, order: IColumn[]) => void;
    getNewColumnsOrderPaired?: (data: IParentData, order: IColumn[]) => void;
    renderExtraColumns?: (id: string) => JSX.Element;
  }

const ModelManagementPairedTableHoc = ({ widgetData, getNewColumnsOrderPaired, lock, tobeDeselected, verticallyHighlighted, initiallySelectedRowsIds, orderSettings, getDraggingStatus, draggingStarted, getNewColumnsOrder, scale, allStatus, hideButtons, clickRowToSelectMode, hideCheckBoxes, hideSearchInput, unblockInput, getSelectedRowsData, dataUrl }: ITableConfig) => {
  const [lastSelectedRecord, setLastSelectedRecord] = useState("");
  const [initialDataCol, setInitialDataCol] = useState(false);
  const authCtx = useContext(AuthContext);

  const { sendRequest: getOverallOrImport, status: getOverallOrImportStatus, data: getOverallOrImportData } = useHttpNonTable(postSingleNonTable, false, authCtx.token);
  const { sendRequest: getRecord, status: getRecordStatus, data: getRecordData } = useHttpNonTable(postSingleNonTable, false, authCtx.token);

  const getRecordHandler = (id: string, sortingQuesry?: string) => getRecord(`field_info${sortingQuesry? sortingQuesry : "?order_by=record_field_name asc"}`, { info_id: id, info_type: widgetData?.info_type }, undefined, "none");
  
  const getOverallOrImportHandler = (selected_record_type: string, specific_record_type: string | undefined, specific_record_id: string | undefined, info_type?: string, sortingQuesry?: string) => {
    getOverallOrImport(`${info_type === "overall" ? "overall_info": "import_info"}${sortingQuesry? sortingQuesry : "?order_by=uploaded_time desc"}`, {
      selected_record_type: selected_record_type,
      specific_record_type:  specific_record_type? specific_record_type : null,
      specific_record_id: specific_record_id? specific_record_id : null,
    },
    undefined, "none");
  };

  useEffect(() => {
    if (
      getOverallOrImportStatus === "completed" &&
      getOverallOrImportData?.length > 0 &&
      !initialDataCol
    ) {
      setInitialDataCol(true);
      const firstRecordId = getOverallOrImportData[0].id;
      setLastSelectedRecord(firstRecordId);
      getRecordHandler(firstRecordId);
    }
  }, [getOverallOrImportStatus, getOverallOrImportData, initialDataCol]); // Added dependencies for correctness

  useEffect(() => {
    if (widgetData?.record_type !== "specific" && widgetData?.record_type && widgetData?.info_type) {
      setLastSelectedRecord("");
      getOverallOrImportHandler(widgetData?.record_type, undefined, undefined, widgetData?.info_type);
    }
  }, [widgetData?.info_type, widgetData?.optionName, widgetData?.record_type, widgetData?.settings?.paired_datamodel_id]);

  useEffect(() => {
    if (widgetData?.record_type === "specific" && widgetData?.settings?.paired_datamodel_id) {
      setLastSelectedRecord("");
      getOverallOrImportHandler(widgetData?.record_type, widgetData?.settings.paired_datamodel_type, widgetData?.settings.paired_datamodel_id, widgetData?.info_type);
    }
  }, [widgetData?.settings?.paired_datamodel_id, widgetData?.info_type, widgetData?.settings?.paired_datamodel_type]);

  useEffect(() => {
    if (widgetData?.record_type === "specific" && widgetData?.record_type && getOverallOrImportData && getOverallOrImportData.length === 1) {
      // edge case
      setLastSelectedRecord(getOverallOrImportData[0].id);
      getRecordHandler(getOverallOrImportData[0].id);
    }
  }, [widgetData?.record_type, widgetData?.info_type, getOverallOrImportData]);

  const sortByImportOrOverall = (sortingQuery: string) => {
    if (widgetData?.record_type !== "specific" && widgetData?.record_type && widgetData?.info_type) {
      getOverallOrImportHandler(widgetData?.record_type, undefined, undefined, widgetData?.info_type, sortingQuery);
    } 
    else if (widgetData?.record_type === "specific" && widgetData?.settings?.paired_datamodel_id) {
      getOverallOrImportHandler(widgetData?.record_type, widgetData?.settings.paired_datamodel_type, widgetData?.settings.paired_datamodel_id, widgetData?.info_type, sortingQuery);
    }
  };

  const sortByRecord = (sortingQuery: string) => {
    getRecordHandler(lastSelectedRecord, sortingQuery);
  };

  const renderTable = () => {
    const resultOveralOrImport = {rows: getOverallOrImportData};
    const resultRecord = {rows: getRecordData};
    const defineDefaultImportOrOveral = widgetData?.info_type && widgetData?.info_type === "overall"? modelManagementModelDataOverallCols : modelManagementModelDataImportCols;
    const defineDefaultImportOrOveralAllCols = widgetData?.info_type && widgetData?.info_type === "overall"? allUniqueManagementModelDataOverallColsKeys : allUniqueManagementModelDataColsKeys;

    const orderAccordingToRemote:IColumn[] | undefined = widgetData?.selectedColumnsPaired?.map((col) => {
      const findKeyOrConvertKeyIntoName = defineDefaultImportOrOveralAllCols[col]? defineDefaultImportOrOveralAllCols[col] : convertKeyIntoName(col);
      return { key: col, displayName: findKeyOrConvertKeyIntoName, dataType: {type: "text" } };
    });

    const orderAccordingToRemoteRecord:IColumn[] | undefined = widgetData?.settings?.selectedColumns?.map((col) => {
      const findKeyOrConvertKeyIntoName = defineDefaultImportOrOveralAllCols[col]? defineDefaultImportOrOveralAllCols[col] : convertKeyIntoName(col);
      return { key: col, displayName: findKeyOrConvertKeyIntoName, dataType: {type: "text" } };
    });

    let defineExtraClassImportOrOveral = "";
    let defineExtraClassRecord = "";

    widgetData?.widgetLayoutSettings?.layout_ratio.forEach((layoutSetting:string) => {
      if (layoutSetting === "1_2") {
        defineExtraClassImportOrOveral = "tableHalf1";
        defineExtraClassRecord = "tableHalf";
      } else if (layoutSetting === "2_3") {
        defineExtraClassImportOrOveral = "tableOneThird";
        defineExtraClassRecord = "tableTwoThird";
      } else if (layoutSetting === "none") {
        defineExtraClassImportOrOveral = "displayNone";
      }
    });

    widgetData?.widgetLayoutSettings?.layout_position.forEach((layoutSetting:string) => {
      if (layoutSetting === "horizontal") {
        if (widgetData?.record_type === "specific" && widgetData?.info_type === "overall") {
          defineExtraClassImportOrOveral = "tableOfOneRow";
          defineExtraClassRecord = "tablePairedWithOneRowTable";
        }

        defineExtraClassImportOrOveral = defineExtraClassImportOrOveral + "B";
        defineExtraClassRecord = defineExtraClassRecord + "B";
      } else if (layoutSetting === "vertical") {
        defineExtraClassImportOrOveral = defineExtraClassImportOrOveral + "R";
        defineExtraClassRecord = defineExtraClassRecord + "R";
      } 
    });

    // show selected only if the call was succesfull
    // lastSelectedRecord

    return (
      <Fragment>
        {validateLoadingStatusNew([getOverallOrImportStatus, getRecordStatus]) && <Spinner />}
       <NormalTable
          sortBy={sortByImportOrOverall}
          draggingStarted={draggingStarted}
          getDraggingStatus={getDraggingStatus}
          columns={orderAccordingToRemote && orderAccordingToRemote.length !== 0 ? orderAccordingToRemote : defineDefaultImportOrOveral}
          verticallyHighlighted={verticallyHighlighted}
          fetchedData={resultOveralOrImport}
          allStatus={allStatus}
          dataUrl={dataUrl}
          orderSettings={orderSettings}
          editColumsOrder={!lock}
          timeFrameIsAstring={true}
          selectedRowId={lastSelectedRecord}
          onRowClickAction={(id:string) => { setLastSelectedRecord(id); getRecordHandler(id);}}
          extraClass={defineExtraClassImportOrOveral}
          getNewColumnsOrder={getNewColumnsOrderPaired}
          defaultSortCol={"uploaded_time"}
          tobeDeselected={tobeDeselected}
          hideSearchInput={hideSearchInput}
          initialySelectedRows={initiallySelectedRowsIds}
          hideCheckBoxes={hideCheckBoxes}
          getSelectedRowsData={getSelectedRowsData}
          unblockInput={unblockInput}
          clickRowToSelectMode={clickRowToSelectMode}
          hideButtons={hideButtons}
          scale={scale}
          allowCollapsedText={true}
          textTruncateLimit={50}
        />
        <NormalTable
          sortBy={sortByRecord}
          draggingStarted={draggingStarted}
          getDraggingStatus={getDraggingStatus}
          columns={orderAccordingToRemoteRecord && orderAccordingToRemoteRecord.length !== 0 ? orderAccordingToRemoteRecord : modelManagementModelDataRecordCols}
          verticallyHighlighted={verticallyHighlighted}
          fetchedData={lastSelectedRecord && getRecordStatus=== "completed" ? resultRecord : {rows : []}}
          allStatus={allStatus}
          dataUrl={dataUrl}
          orderSettings={orderSettings}
          editColumsOrder={!lock}
          extraClass={defineExtraClassRecord}
          timeFrameIsAstring={true}
          getNewColumnsOrder={getNewColumnsOrder}
          defaultSortCol={"record_field_name"}
          tobeDeselected={tobeDeselected}
          hideSearchInput={hideSearchInput}
          initialySelectedRows={initiallySelectedRowsIds}
          hideCheckBoxes={hideCheckBoxes}
          getSelectedRowsData={getSelectedRowsData}
          unblockInput={unblockInput}
          clickRowToSelectMode={clickRowToSelectMode}
          hideButtons={hideButtons}
          scale={scale}
        />
      </Fragment>
    );
  };

  return <Fragment>{renderTable()}</Fragment>;
};

export default ModelManagementPairedTableHoc;